import { uiText } from '../../i18n/uiText';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  X, Tag, Star, CheckCircle, Clock,
  UserCheck, Users, ChevronRight, AlertCircle,
} from 'lucide-react';
import { auditorPoolApi, auditAssignmentsApi, getApiErrorMessage } from '../../api';
import Dialog from '../common/Dialog';
import FormField, { TextInput, SelectInput, TextArea } from '../common/FormField';
import { useAuth } from '../../store/AuthContext';
import useDraftBuffer from '../../hooks/useDraftBuffer';
import FormDraftNotice from '../common/FormDraftNotice';

const SPECIALIZATIONS = [
  { value: 'supplier_audit',   label: uiText("Аудит поставщиков") },
  { value: 'process_audit',    label: uiText("Аудит процессов") },
  { value: 'quality_systems',  label: uiText("Системы менеджмента качества") },
  { value: 'food_safety',      label: uiText("Безопасность пищевой продукции") },
  { value: 'env_audit',        label: uiText("Экологический аудит") },
  { value: 'safety_audit',     label: uiText("Охрана труда и безопасность") },
  { value: 'financial_audit',  label: uiText("Финансовый аудит") },
  { value: 'it_audit',         label: uiText("ИТ-аудит") },
  { value: 'procurement',      label: uiText("Закупочная деятельность") },
  { value: 'construction',     label: uiText("Строительство и эксплуатация") },
  { value: 'extended_audit',   label: uiText("Аудиты с расширенным регламентом") },
  { value: 'compliance',       label: uiText("Комплаенс / нормативное соответствие") },
  { value: 'internal_control', label: uiText("Внутренний контроль") },
  { value: 'risk_management',  label: uiText("Управление рисками") },
];

const TEAM_ROLES = [
  { value: 'lead', label: uiText("Ведущий аудитор") },
  { value: 'expert', label: uiText("Эксперт") },
  { value: 'observer', label: uiText("Наблюдатель") },
];

function specLabel(val) {
  return SPECIALIZATIONS.find(s => s.value === val)?.label || val;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('');
}

function normalizeTeamRoles(selectedIds, seed = {}) {
  const ids = Array.from(selectedIds);
  const next = {};
  ids.forEach((id, index) => {
    next[id] = seed[id] || (index === 0 ? 'lead' : 'expert');
  });
  const leadId = ids.find((id) => next[id] === 'lead');
  if (!leadId && ids.length) {
    next[ids[0]] = 'lead';
  } else {
    ids.forEach((id) => {
      if (id !== leadId && next[id] === 'lead') next[id] = 'expert';
    });
  }
  return next;
}

function AuditorMiniCard({ profile, selected, role, onRoleChange, onToggle, assignment, disabled }) {
  const specs = Array.isArray(profile.specializations)
    ? profile.specializations
    : (profile.specializations || '').split(',').map(s => s.trim()).filter(Boolean);

  const isStaff = profile.auditor_type !== 'contracted';

  return (
    <div
      className={`ap-assignee${selected ? ' is-selected' : ''}`}
    >
      <label className="ap-assignee__select"><input type="checkbox" className="ap-check" checked={selected} disabled={disabled} onChange={() => onToggle(profile.user_id)} /><span>{profile.full_name}</span></label>

      <div className="ap-assignee__identity">
        <div className="ap-assignee__avatar" aria-hidden="true">
          {getInitials(profile.full_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ap-assignee__detail">
            {profile.job_title || (isStaff ? uiText("Штатный аудитор") : uiText("Привлекаемый"))}
          </div>
        </div>
      </div>

      {profile.experience_years != null && (
        <div className="ap-assignee__detail ap-inline-actions">
          <Star size={10} /> {profile.experience_years}{uiText(" лет опыта")}</div>
      )}

      {specs.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {specs.slice(0, 3).map(s => (
            <span className="ap-badge" key={s}>
              <Tag size={8} />{specLabel(s)}
            </span>
          ))}
          {specs.length > 3 && (
            <span className="ap-assignee__detail">+{specs.length - 3}</span>
          )}
        </div>
      )}

      {assignment && (
        <div className={`ap-badge ${assignment.status === 'accepted' ? 'ap-badge--success' : 'ap-badge--warning'}`}>
          <Clock size={9} /> {assignment.status === 'accepted' ? uiText("Уже подтвержден") : assignment.status === 'pending' ? uiText("Ожидает ответа") : uiText("Ранее отказался")}
        </div>
      )}

      {selected && (
        <FormField label={uiText("Роль: {v0}", {v0: (profile.full_name)})}>
          <SelectInput
            disabled={disabled}
            value={role || 'expert'}
            onChange={(event) => onRoleChange(profile.user_id, event.target.value)}
          >
            {TEAM_ROLES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </SelectInput>
        </FormField>
      )}

      {profile.available_from && (
        <div className="ap-badge ap-badge--success">
          <CheckCircle size={9} />{uiText(" Свободен с ")}{String(profile.available_from).slice(0, 10)}
        </div>
      )}
    </div>
  );
}

export default function AssignAuditorsModal({ audit, onClose, onAssigned }) {
  const { user } = useAuth();
  const buffer = useDraftBuffer(user?.id, audit.id, 'audit-team');
  const [profiles, setProfiles] = useState([]);
  const [existing, setExisting] = useState(new Set());
  const [existingMap, setExistingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const team = buffer.values.team || { selected: [], roles: {}, note: '' };
  const selected = new Set(team.selected);
  const roles = team.roles;
  const note = team.note;
  const editTeam = (key, update) => {
    if (saving || buffer.recovery) return;
    const current = buffer.values.team || team;
    buffer.edit('team', { ...current, [key]: typeof update === 'function' ? update(current[key]) : update });
  };
  const setSelected = value => editTeam('selected', [...value]);
  const setRoles = update => editTeam('roles', update);
  const setNote = value => editTeam('note', value);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setReady(false);
    Promise.all([
      auditorPoolApi.list(),
      auditAssignmentsApi.list(audit.id),
    ]).then(([poolRes, assignRes]) => {
      if (!active) return;
      setProfiles(Array.isArray(poolRes.data) ? poolRes.data : []);
      const assignments = Array.isArray(assignRes.data) ? assignRes.data : [];
      const existingIds = new Set(assignments.filter(a => a.status !== 'revoked').map(a => a.auditor_user_id));
      setExisting(existingIds);
      setExistingMap(Object.fromEntries(assignments.map(a => [a.auditor_user_id, a])));
      buffer.hydrate({ team: { selected: [...existingIds], roles: normalizeTeamRoles(existingIds, Object.fromEntries(assignments.map(a => [a.auditor_user_id, a.team_role || 'expert']))), note: '' } });
      setReady(true);
    }).catch(e => { if (active) setError(getApiErrorMessage(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [audit.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter(p => {
      if (!q) return true;
      const specs = Array.isArray(p.specializations) ? p.specializations : (p.specializations || '').split(',').map(s => s.trim()).filter(Boolean);
      return (
        (p.full_name || '').toLowerCase().includes(q) ||
        (p.job_title || '').toLowerCase().includes(q) ||
        specs.some(s => specLabel(s).toLowerCase().includes(q))
      );
    });
  }, [profiles, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
    setRoles((prev) => normalizeTeamRoles(next, prev));
  };

  const handleRoleChange = (id, value) => {
    setRoles((prev) => {
      const seed = { ...prev, [id]: value };
      if (value === 'lead') {
        Array.from(selected).forEach((selectedId) => {
          if (selectedId !== id && seed[selectedId] === 'lead') seed[selectedId] = 'expert';
        });
      }
      return normalizeTeamRoles(selected, seed);
    });
  };

  const handleAssign = async () => {
    if (busy.current || !ready || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      await auditAssignmentsApi.assign(audit.id, {
        auditors: Array.from(selected).map((id) => ({
          auditor_user_id: id,
          team_role: roles[id] || 'expert',
          note: note.trim() || null,
        })),
        note: note.trim() || null,
      });
      buffer.acknowledge(snapshot, { committed: true });
      if (buffer.active) { await onAssigned?.(); onClose(); }
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const newlySelected = [...selected].filter(id => !existing.has(id));

  return (
    <Dialog onClose={onClose} closeDisabled={saving} size="lg" label={uiText("Назначить аудиторов")}>
        <div className="ap-dialog__header">
          <div>
            <div className="ap-dialog__title" data-dialog-heading tabIndex={-1}>
              <UserCheck size={18} />{uiText("Назначить аудиторов")}</div>
            <p className="ap-dialog__description">{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</p>
          </div>
          <button type="button" className="ap-button ap-button--secondary ap-button--sm" aria-label={uiText("Закрыть")} title={uiText("Закрыть")} disabled={saving} onClick={onClose}><X size={16} /></button>
        </div>

        <div className="ap-dialog__body ap-stack">
          <FormDraftNotice buffer={buffer} label={uiText("Команда аудита")} disabled={saving || loading} />
          <FormField label={uiText("Поиск аудитора")}>
            <TextInput
              placeholder={uiText("Поиск по имени или специализации…")}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </FormField>
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <Users size={32} color="#ddd" />
              <p>{uiText("Аудиторы не найдены")}</p>
            </div>
          ) : (
            <div className="ap-assignee-grid">
              {filtered.map(p => (
                <AuditorMiniCard
                  key={p.user_id}
                  profile={p}
                  selected={selected.has(p.user_id)}
                  role={roles[p.user_id] || existingMap[p.user_id]?.team_role || 'expert'}
                  onRoleChange={handleRoleChange}
                  onToggle={toggle}
                  assignment={existingMap[p.user_id]}
                  disabled={saving}
                />
              ))}
            </div>
          )}
          <FormField label={uiText("Примечание для аудиторов (необязательно)")}>
            <TextArea
              disabled={saving}
              rows={2}
              placeholder={uiText("Например: аудит на объекте в Санкт-Петербурге, требуется выезд на 3 дня…")}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </FormField>
            <div className="ap-assignee__detail" role="status">{uiText("Выбрано: ")}<strong>{selected.size}</strong>{uiText(" аудитор(ов)")}{newlySelected.length > 0 && (
                <span>
                  (+{newlySelected.length}{uiText(" новых приглашений)")}</span>
              )}
            </div>
        </div>
          {error && <div className="ap-save-notice ap-save-notice--warning ap-dialog__alert" role="alert"><AlertCircle size={18} />{error}</div>}
            <div className="ap-dialog__footer">
              <button type="button" className="ap-button ap-button--secondary" disabled={saving} onClick={onClose}>{uiText("Отмена")}</button>
              <button type="button" className="ap-button ap-button--secondary" disabled={saving || !ready} onClick={() => { setSelected(new Set()); setRoles({}); }}>{uiText("Снять всех")}</button>
              <button type="button"
                className="ap-button ap-button--primary"
                onClick={handleAssign}
                disabled={saving || !ready}
              >
                {saving ? uiText("Сохранение…") : (
                  <><ChevronRight size={15} />{uiText(" Назначить и отправить уведомления")}</>
                )}
              </button>
            </div>
    </Dialog>
  );
}
